import React, {useReducer, useState} from 'react'
import gql from 'graphql-tag'
import {useMutation} from '@apollo/react-hooks'
import {Button, Checkbox, Input, notification} from 'antd'
import ApproveProductChanges from 'components/shared/ApproveProductChanges'
import Product from './Product'

import './index.css'

const defaultState = {
  keyPickupText: 'Hent nøkkel på meglerkontor'
}

const updateQuantityMutation = gql`
  mutation UpdateProductQuantity($input: UpdateProductQuantityInput!) {
    UpdateProductQuantity(input: $input) {
      product {
        id
        ...on PhotoBasedProduct { quantity }
        ...on MeasurementProduct { quantity }
      }
    }
  }
`

const cancelProductMutation = gql`
  mutation CancelProduct($input: CancelProductInput!) {
    CancelProduct(input: $input) {
      product {
        id
        state
      }
    }
  }
`

const addExpressMutation = gql`
  mutation AddExpressDeliveryToBooking($id: ID!) {
    AddExpressDeliveryToBooking(id: $id) {
      booking {
        id
        expressDelivery
      }
    }
  }
`

const addKeyPickupMutation = gql`
  mutation AddKeyPickupToBooking($id: ID!, $text: String!) {
    AddKeyPickupToBooking(id: $id, instructions: $text) {
      booking {
        id
        pickUpKeysInstructions
      }
    }
  }
`

function productsReducer(state, action) {
  const newState = {...state}
  if (action.product && !newState[action.product.id]) {
    newState[action.product.id] = {}
  }

  switch (action.type) {
    case 'SET_QUANTITY':
      newState[action.product.id].quantity = Number(action.quantity)
      return newState

    case 'TOGGLE_CANCELLED':
      newState[action.product.id].cancelled = !!action.cancelled
      return newState

    case 'TOGGLE_EXPRESS':
      newState.express = !!action.enabled
      return newState

    case 'TOGGLE_KEY_PICKUP':
      newState.keyPickup = !!action.enabled
      return newState

    case 'SET_KEY_PICKUP_TEXT':
      newState.keyPickupText = action.text
      return newState

    case 'RESET':
      return {...defaultState}

    default:
      throw new Error(`Unknown action ${action.type}`)
  }
}

const ManageProducts = ({booking}) => {
  const [changes, reducer] = useReducer(productsReducer, {...defaultState})
  const [loading, setIsLoading] = useState(false)
  const [updateQuantity] = useMutation(updateQuantityMutation)
  const [cancelProduct] = useMutation(cancelProductMutation)
  const [addExpressDelivery] = useMutation(addExpressMutation, {variables: {id: booking.id}})
  const [addKeyPickup] = useMutation(addKeyPickupMutation, {variables: {id: booking.id}})

  const products = booking.products.edges.map(e => e.node)
  const canBeSubmitted = Object.keys(changes).length > 0 &&
    (!changes.keyPickup || (changes.keyPickup && changes.keyPickupText))

  function getProductValue(product, key) {
    if (changes[product.id] && changes[product.id][key]) {
      return changes[product.id][key]
    }
    return product[key]
  }

  function reset() {
    reducer({type: 'RESET'})
  }

  async function submit(approval) {
    setIsLoading(true)
    const mutations = []
    if (changes.express) {
      mutations.push(addExpressDelivery())
    }
    if (changes.keyPickup) {
      mutations.push(addKeyPickup({variables: {text: changes.keyPickupText}}))
    }
    Object.keys(changes).forEach(productId => {
      const product = products.find(p => p.id === productId)
      if (!product) return

      if (changes[productId].cancelled) {
        mutations.push(
          cancelProduct({variables: {input: {productId, approval}}})
        )
      }
      if (changes[productId].quantity && changes[productId].quantity > product.quantity) {
        mutations.push(
          updateQuantity({variables: {input: {productId, approval, quantity: changes[productId].quantity}}})
        )
      }
    })

    let hasError = false
    await Promise.all(mutations).catch(() => {
      hasError = true
    })

    if (hasError) {
      notification.error({
        message: 'Kunne ikke lagre alle endringer',
        description: 'Noen av endringene kunne ikke gjennomføres. Forsøk på nytt eller kontakt EFKT.',
      })
    } else {
      notification.success({
        message: 'Alle produktendringer lagret',
      })
    }

    setIsLoading(false)
    reset()
  }

  return (
    <div className="manage-products">
      <div className="row head">
        <span className="name">Product</span>
        <span className="quantity">Antall</span>
        <span className="cancel">Kanseller</span>
      </div>

      {products.map(product => (
        <Product
          key={product.id}
          product={product}
          getProductValue={key => getProductValue(product, key)}
          reducer={reducer}
        />
      ))}

      <div className="key-pickup">
        <Checkbox
          disabled={!!booking.pickUpKeysInstructions}
          checked={!!booking.pickUpKeysInstructions || changes.keyPickup}
          onChange={e => reducer({type: 'TOGGLE_KEY_PICKUP', enabled: e.target.checked})}
        >
          Nøkkelhenting
        </Checkbox>
        <Input
          allowClear
          placeholder="Må fylle ut"
          value={booking.pickUpKeysInstructions || changes.keyPickupText}
          disabled={!!booking.pickUpKeysInstructions || !changes.keyPickup}
          onChange={e => reducer({type: 'SET_KEY_PICKUP_TEXT', text: e.target.value})}
        />
      </div>
      <div className="express">
        <Checkbox
          disabled={booking.expressDelivery}
          checked={booking.expressDelivery || changes.express}
          onChange={e => reducer({type: 'TOGGLE_EXPRESS', enabled: e.target.checked})}
        >
          Levering samme dag (må laste opp før kl. 17)
        </Checkbox>
      </div>

      <ApproveProductChanges
        hasChanges={canBeSubmitted}
        brokerPhone={booking.estate.broker.phone}
        onSubmit={submit}
        isSubmitting={loading}
        additionalButtons={
          <Button
            disabled={loading}
            onClick={reset}
          >
            Tilbakestill endringer
          </Button>
        }
      />
    </div>
  )
}

export default ManageProducts
